import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import {
  resolveMediaUrl,
  handleMediaImgError,
} from "../Helper/mediaUrl";
import AdPlacement from "../Advertising/AdPlacement";
import "./CommunityPromoRailAd.css";

const SIDEBAR_AD_MAX_SCALE = 1;

/** Fits the rail ad's selected unit (DisplayAd data-ad-*) to the host width; never upscales. */
function useSidebarAdScale(hostRef) {
  useEffect(() => {
    const host = hostRef.current;
    if (!host || typeof window === "undefined" || typeof window.ResizeObserver !== "function") {
      return undefined;
    }
    const setVar = (prop, value) => {
      if (host.style.getPropertyValue(prop) !== value) host.style.setProperty(prop, value);
    };
    const sync = () => {
      const unit = host.querySelector(".display-ad[data-ad-width][data-ad-height]");
      const unitWidth = unit ? Number(unit.getAttribute("data-ad-width")) : 0;
      const unitHeight = unit ? Number(unit.getAttribute("data-ad-height")) : 0;
      const width = host.getBoundingClientRect().width;
      if (!(unitWidth > 0 && unitHeight > 0 && width > 0)) return;
      // Rounded down so the scaled creative never exceeds the fractional host width.
      const scale = Math.min(SIDEBAR_AD_MAX_SCALE, Math.floor((width / unitWidth) * 10000) / 10000);
      setVar("--sidebar-ad-max-width", `${unitWidth}px`);
      setVar("--sidebar-ad-ratio", `${unitWidth} / ${unitHeight}`);
      setVar("--sidebar-ad-scale", scale.toFixed(4));
      // Native iframe size + scaled (fit) box: transform scaling, which WebKit
      // applies to iframe content (it ignores `zoom` there).
      setVar("--sidebar-ad-width", `${unitWidth}px`);
      setVar("--sidebar-ad-height", `${unitHeight}px`);
      setVar("--sidebar-ad-fit-width", `${Math.floor(unitWidth * scale * 100) / 100}px`);
      setVar("--sidebar-ad-fit-height", `${Math.floor(unitHeight * scale * 100) / 100}px`);
    };
    const resizeObserver = new window.ResizeObserver(sync);
    resizeObserver.observe(host);
    const unitObserver =
      typeof window.MutationObserver === "function" ? new window.MutationObserver(sync) : null;
    if (unitObserver) {
      unitObserver.observe(host, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["data-ad-width", "data-ad-height"],
      });
    }
    sync();
    return () => {
      resizeObserver.disconnect();
      if (unitObserver) unitObserver.disconnect();
    };
  }, [hostRef]);
}

/**
 * Right-rail promotions — featured orgs/creators/projects/events, never empty.
 */
function CommunityPromoRail({
  organizations = [],
  creators = [],
  projects = [],
  loading = false,
}) {
  const adHostRef = useRef(null);
  useSidebarAdScale(adHostRef);
  const featuredOrgs = (organizations || []).filter((o) => o && o.is_featured).slice(0, 3);
  const orgCards = featuredOrgs.length
    ? featuredOrgs
    : (organizations || []).slice(0, 3);
  const creatorCards = (creators || [])
    .filter((c) => c && (c.is_featured || true))
    .slice(0, 2);
  const projectCards = (projects || []).slice(0, 2);

  const blocks = [];

  if (orgCards.length) {
    blocks.push({
      key: "orgs",
      title: "Featured Organizations",
      items: orgCards.map((item) => ({
        id: item.id || item.slug,
        name: item.name,
        meta: item.organization_type_label || "Organization",
        image: item.logo_path || item.cover_image_path,
        path: item.path || item.public_path,
      })),
    });
  }

  if (creatorCards.length) {
    blocks.push({
      key: "creators",
      title: "Featured Creators",
      items: creatorCards.map((item) => ({
        id: item.id || item.slug,
        name: item.name || item.display_name,
        meta: "Creator",
        image: item.avatar || item.logo_path,
        path: item.path || item.public_path || `/creator/${item.slug}`,
      })),
    });
  }

  if (projectCards.length) {
    blocks.push({
      key: "projects",
      title: "Sponsored Projects",
      items: projectCards.map((item) => ({
        id: item.id || item.slug,
        name: item.name || item.title,
        meta: "Project",
        image: item.cover_image_path || item.logo_path || item.banner,
        path: item.path || item.public_path || `/project/${item.slug}`,
      })),
    });
  }

  if (!blocks.length && !loading) {
    blocks.push({
      key: "house",
      title: "Pentecost+",
      items: [
        {
          id: "home",
          name: "Explore formation on Home",
          meta: "House",
          image: null,
          path: "/home",
        },
        {
          id: "orgs",
          name: "Browse organizations",
          meta: "Directory",
          image: null,
          path: "/community/organizations",
        },
        {
          id: "projects",
          name: "Discover community projects",
          meta: "Projects",
          image: null,
          path: "/community/projects",
        },
      ],
    });
  }

  return (
    <aside className="community-promo-rail" aria-label="Promotions">
      <p className="community-promo-rail__eyebrow">Discover</p>
      {loading && !blocks.length ? (
        <div className="community-promo-rail__skeleton" role="status">
          Loading recommendations…
        </div>
      ) : null}
      {blocks.map((block) => (
        <section key={block.key} className="community-promo-rail__block">
          <h2 className="community-promo-rail__title">{block.title}</h2>
          <ul className="community-promo-rail__list">
            {block.items.map((item) => (
              <li key={item.id || item.name} className="community-promo-rail__item">
                <Link to={item.path || "/community"} className="community-promo-rail__card">
                  <span className="community-promo-rail__media" aria-hidden="true">
                    {item.image ? (
                      <img
                        src={resolveMediaUrl(item.image)}
                        alt=""
                        onError={handleMediaImgError}
                      />
                    ) : (
                      <span>{(item.name || "?").charAt(0).toUpperCase()}</span>
                    )}
                  </span>
                  <span className="community-promo-rail__copy">
                    <span className="community-promo-rail__meta">{item.meta}</span>
                    <span className="community-promo-rail__name">{item.name}</span>
                  </span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      ))}
      <p className="community-promo-rail__events community-promo-rail__events--muted">
        Discover more in Organizations and Projects.
      </p>
      <div className="community-promo-rail__ad" ref={adHostRef}>
        <AdPlacement placementKey="community_sidebar" limit={1} />
      </div>
    </aside>
  );
}

export default CommunityPromoRail;
